import { DebugElement } from '@angular/core';
import { ComponentFixture } from '@angular/core/testing';
import { By } from '@angular/platform-browser';
import {} from 'jasmine';
import AppSettings from 'src/app/common/app.settings';
import { ImagePreloadDirective } from 'src/app/common/directives/image-preload/image-preload.directive';
import { LocationObject, Viewport } from '../swagger-api';

export const fakeEvent: Partial<Event> = {
  preventDefault: () => {
    //Simply ignore
  },
  stopPropagation: () => {
    //Simply ignore
  },
};

export const locationMock: LocationObject = {
  country: {
    alpha2: 'CA',
    full_name: 'Canada',
  },
  region: '',
  city: 'Vancouver',
  street: '',
  center: { lat: 49.246292, lon: -123.116226 },
};

export interface TotalCountRepresentation {
  totalCount: number;
  representation: string;
}

export const totalCountSet: TotalCountRepresentation[] = [
  {
    totalCount: 0,
    representation: '0',
  },
  {
    totalCount: 1,
    representation: '1',
  },
  {
    totalCount: 40,
    representation: '40',
  },
  {
    totalCount: 110,
    representation: '110',
  },
  {
    totalCount: 3000,
    representation: '3,000',
  },
  {
    totalCount: 45001,
    representation: '45,001',
  },
  {
    totalCount: 450010,
    representation: '450,010',
  },
  {
    totalCount: 4500100,
    representation: '4,500,100',
  },
  {
    totalCount: 45001000,
    representation: '45,001,000',
  },
  {
    totalCount: 450010000,
    representation: '450,010,000',
  },
];

export const viewportMock: Viewport = {
  northeast: { lat: 49.5699736, lon: -122.4080664 },
  southwest: { lat: 49.00205589999999, lon: -123.7148273 },
};

export function findElement<T>(fixture: ComponentFixture<T>, selector: string): DebugElement {
  return fixture.debugElement.query(By.css(selector));
}

export function findNativeElement<T>(fixture: ComponentFixture<T>, selector: string): HTMLElement {
  return findElement(fixture, selector).nativeElement as HTMLElement;
}

/** @deprecated
 * Install a spy onto an existing object's private method dynamically (no type checking).
 * @param object The object upon which to install the `Spy`.
 * @param method The name of the method to replace with a `Spy`.
 */
export function dynamicSpyOn<T>(object: T, method: string): jasmine.Spy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
  return spyOn<any>(object, method);
}

export function findElementByDataTestId<T>(fixture: ComponentFixture<T>, id: string): HTMLElement {
  return fixture.debugElement.query(By.css(`[data-test-id="${id}"]`)).nativeElement as HTMLElement;
}

export function findDebugElementByDataTestId<T>(fixture: ComponentFixture<T>, id: string): DebugElement {
  return fixture.debugElement.query(By.css(`[data-test-id="${id}"]`));
}

export function checkHint<T>(fixture: ComponentFixture<T>, selector: string, expectedHint: string): void {
  const element: HTMLElement = findNativeElement(fixture, selector);
  expect(element.innerText.trim()).toEqual(expectedHint);
}

/**
 * Finds html element by its data-test-id
 * @param { ComponentFixture<T> } fixture
 * @param { string } id
 */

export function getElemById<T, K extends HTMLElement>(fixture: ComponentFixture<T>, id: string): K {
  return (fixture.nativeElement as K).querySelector(`[data-test-id="${id}"]`);
}

export function mockProperty<T, V>(object: T, prop: string, value: V): T {
  Object.defineProperty(object, prop, {
    enumerable: false,
    configurable: false,
    writable: false,
    value,
  });
  return object;
}

export function errorImgUpdate<T>(
  fixture: ComponentFixture<T>,
  selector: string,
  directive?: ImagePreloadDirective,
): void {
  const img: HTMLImageElement = findNativeElement(fixture, selector) as HTMLImageElement;
  fixture.detectChanges();

  spyOn(directive, 'updateUrl').and.callThrough();
  img.dispatchEvent(new Event('error'));
  fixture.detectChanges();

  expect(img.src).toContain(AppSettings.NO_PHOTO_DEFAULT_IMAGE);
}

export function issueLocalStorageEvent(key: string, oldValue?: string, newValue?: string): void {
  window.dispatchEvent(
    new StorageEvent('storage', {
      storageArea: localStorage,
      key,
      newValue,
      oldValue,
    }),
  );
}

export function emitClickOnElement<T>(fixture: ComponentFixture<T>, selector: string): void {
  findNativeElement(fixture, selector).click();
}

export function clickHandler<T>(fixture: ComponentFixture<T>, id: string): void {
  emitClickOnElement(fixture, `[data-test-id="${id}"]`);
}

export function checkVisible<T>(fixture: ComponentFixture<T>, dataTestId: string, visible: boolean): void {
  const element: HTMLElement | null = getElemById(fixture, dataTestId);
  visible ? expect(element).toBeTruthy() : expect(element).toBeNull();
}

export function checkInputDisabled<T>(fixture: ComponentFixture<T>, dataTestId: string, isDisabled: boolean): void {
  const element: HTMLInputElement | null = getElemById<T, HTMLInputElement>(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.disabled).toEqual(isDisabled);
}

export function checkInputReadonly<T>(fixture: ComponentFixture<T>, dataTestId: string, isReadOnly: boolean): void {
  const element: HTMLInputElement | null = getElemById<T, HTMLInputElement>(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.readOnly).toEqual(isReadOnly);
}

export function checkInnerText<T>(fixture: ComponentFixture<T>, dataTestId: string, expectedInnerText: string): void {
  const element = getElemById(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.innerText).toEqual(expectedInnerText);
}

export function checkInnerHtml<T>(fixture: ComponentFixture<T>, dataTestId: string, expectedInnerHtml: string): void {
  const element = getElemById(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.innerHTML.trim()).toEqual(expectedInnerHtml);
}

export function checkImageSrc<T>(fixture: ComponentFixture<T>, dataTestId: string, expectedSrc: string): void {
  const element: HTMLImageElement = getElemById<T, HTMLImageElement>(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.src).toEqual(expectedSrc);
}

type SupportedInputElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

type SupportedDisabledElement =
  | HTMLInputElement
  | HTMLButtonElement
  | HTMLTextAreaElement
  | HTMLOptionElement
  | HTMLOptGroupElement
  | HTMLSelectElement;

export function setFieldElementValue(element: SupportedInputElement, value: string): void {
  element.value = value;
  element.dispatchEvent(new Event('input'));
  // Dispatch an `input` or `change` fake event
  // so Angular form bindings take notice of the change.
  const isSelect: boolean = element instanceof HTMLSelectElement;
  const event: Event = new Event(isSelect ? 'change' : 'input', { bubbles: !isSelect } as EventInit);
  element.dispatchEvent(event);
}

export function setFieldValue<T>(fixture: ComponentFixture<T>, dataTestId: string, value: string): void {
  const element: SupportedInputElement = getElemById<T, SupportedInputElement>(fixture, dataTestId);
  expect(element).not.toBeNull();
  setFieldElementValue(element, value);
}

export function checkElementDisabled<T>(fixture: ComponentFixture<T>, dataTestId: string, isDisabled: boolean): void {
  const element: SupportedDisabledElement | null = getElemById<T, SupportedDisabledElement>(fixture, dataTestId);
  expect(element).not.toBeNull();
  expect(element.disabled).toEqual(isDisabled);
}

export const hasClasses = (el: HTMLElement, classes: string[]): boolean =>
  classes.every((cls) => el.classList.contains(cls));
